/**
 * MapsChoiceModal Component
 *
 * A centered card modal that lets users choose between Google Maps and Apple Maps.
 * Uses FLIP animation to expand from the source button and collapse back to it.
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './MapsChoiceModal.module.css';

interface MapsChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectGoogle: () => void;
  onSelectApple: () => void;
  /** Source element rect for FLIP animation (position of clicked button) */
  sourceRect: DOMRect | null;
  /** Client storefront uses the shared glass sheet; admin keeps the legacy card. */
  variant?: 'legacy' | 'client';
}

export function MapsChoiceModal({
  variant = 'legacy',
  ...props
}: MapsChoiceModalProps) {
  return <LegacyMapsChoiceModal {...props} clientVariant={variant === 'client'} />;
}

function LegacyMapsChoiceModal({
  isOpen,
  onClose,
  onSelectGoogle,
  onSelectApple,
  sourceRect,
  clientVariant,
}: MapsChoiceModalProps & { clientVariant: boolean }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [isClosing, setIsClosing] = useState(false);

  // Track if modal is active (open or closing)
  const isActive = isOpen || isClosing;

  // Delay backdrop visibility for transition
  const [backdropVisible, setBackdropVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        setBackdropVisible(true);
      });
    } else {
      setBackdropVisible(false);
      setIsClosing(false);
    }
  }, [isOpen]);

  // ─────────────────────────────────────────────────────────────────────────
  // FLIP Animation - Expand from source button to center
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !sourceRect || !wrapperRef.current || !cardRef.current) {
      return;
    }

    const wrapper = wrapperRef.current;
    const card = cardRef.current;

    // Small delay to let content settle
    const timeoutId = setTimeout(() => {
      // Get the wrapper's centered position (the "Last" in FLIP)
      const wrapperRect = wrapper.getBoundingClientRect();

      // Calculate center points
      const sourceCenter = {
        x: sourceRect.left + sourceRect.width / 2,
        y: sourceRect.top + sourceRect.height / 2,
      };
      const wrapperCenter = {
        x: wrapperRect.left + wrapperRect.width / 2,
        y: wrapperRect.top + wrapperRect.height / 2,
      };

      // Calculate deltas in pure pixels
      const deltaX = sourceCenter.x - wrapperCenter.x;
      const deltaY = sourceCenter.y - wrapperCenter.y;

      // Calculate scale based on source size vs card size
      const scaleX = sourceRect.width / wrapperRect.width;
      const scaleY = sourceRect.height / wrapperRect.height;
      const scale = Math.min(scaleX, scaleY, 0.3); // Cap at 0.3 for nice effect

      // INVERT: Position card at source location
      card.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scale})`;
      card.style.opacity = '0';

      // Force reflow
      void card.offsetHeight;

      // PLAY: Animate to final position
      requestAnimationFrame(() => {
        card.classList.add(styles.animating);
        card.style.transform = 'translate(0px, 0px) scale(1)';
        card.style.opacity = '1';
      });
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [isOpen, sourceRect]);

  // Reset card styles when fully closed
  useEffect(() => {
    if (!isActive && cardRef.current) {
      cardRef.current.classList.remove(styles.animating);
      cardRef.current.style.transform = '';
      cardRef.current.style.opacity = '';
    }
  }, [isActive]);

  // ─────────────────────────────────────────────────────────────────────────
  // Reverse FLIP Animation - Collapse back to source button
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isClosing || !sourceRect || !wrapperRef.current || !cardRef.current) {
      return;
    }

    const wrapper = wrapperRef.current;
    const card = cardRef.current;

    // Get current center position
    const wrapperRect = wrapper.getBoundingClientRect();

    // Calculate centers
    const sourceCenter = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const wrapperCenter = {
      x: wrapperRect.left + wrapperRect.width / 2,
      y: wrapperRect.top + wrapperRect.height / 2,
    };

    // Calculate deltas (from center back to source)
    const deltaX = sourceCenter.x - wrapperCenter.x;
    const deltaY = sourceCenter.y - wrapperCenter.y;

    // Calculate scale
    const scaleX = sourceRect.width / wrapperRect.width;
    const scaleY = sourceRect.height / wrapperRect.height;
    const scale = Math.min(scaleX, scaleY, 0.3);

    // Start at current position
    card.style.transform = 'translate(0px, 0px) scale(1)';
    card.style.opacity = '1';

    // Force reflow
    void card.offsetHeight;

    // Animate back to source
    requestAnimationFrame(() => {
      card.classList.add(styles.animating);
      card.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scale})`;
      card.style.opacity = '0';
    });
  }, [isClosing, sourceRect]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setBackdropVisible(false);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 250);
  }, [onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isActive]);

  // Focus close button on open
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  const handleGoogleClick = useCallback(() => {
    onSelectGoogle();
    handleClose();
  }, [onSelectGoogle, handleClose]);

  const handleAppleClick = useCallback(() => {
    onSelectApple();
    handleClose();
  }, [onSelectApple, handleClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  // Don't render if not active
  if (!isActive) return null;

  const backdropClass = [
    styles.backdrop,
    clientVariant && styles.clientBackdrop,
    backdropVisible && styles.visible,
    isClosing && styles.closing,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {/* Backdrop */}
      <div
        className={backdropClass}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Centering Wrapper - handles positioning, never animates */}
      <div ref={wrapperRef} className={styles.wrapper}>
        {/* Card - does FLIP with pure pixel transforms */}
        <div
          ref={cardRef}
          className={`${styles.card} ${clientVariant ? styles.clientCard : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="maps-choice-title"
          tabIndex={-1}
          data-testid="maps-choice-modal"
        >
          {/* Close Button */}
          <button
            ref={closeButtonRef}
            type="button"
            className={`${styles.closeBtn} ${clientVariant ? styles.clientCloseBtn : ''}`}
            onClick={handleClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>

          {/* Title */}
          {clientVariant && <div className={styles.clientKicker}>Directions</div>}
          <h2 id="maps-choice-title" className={`${styles.title} ${clientVariant ? styles.clientTitle : ''}`}>
            {clientVariant ? 'Open in maps' : 'Open with'}
          </h2>

          {/* Options */}
          <div className={`${styles.options} ${clientVariant ? styles.clientOptions : ''}`}>
            <button
              type="button"
              className={`${styles.option} ${clientVariant ? styles.clientOption : ''}`}
              onClick={handleGoogleClick}
              data-testid="maps-choice-google"
            >
              <div className={`${styles.optionIcon} ${clientVariant ? styles.clientOptionIcon : ''}`}>
                <GoogleMapsIcon />
              </div>
              <span className={`${styles.optionName} ${clientVariant ? styles.clientOptionName : ''}`}>Google Maps</span>
              {clientVariant && <ChevronIcon />}
            </button>
            <div className={`${styles.divider} ${clientVariant ? styles.clientDivider : ''}`} />
            <button
              type="button"
              className={`${styles.option} ${clientVariant ? styles.clientOption : ''}`}
              onClick={handleAppleClick}
              data-testid="maps-choice-apple"
            >
              <div className={`${styles.optionIcon} ${clientVariant ? styles.clientOptionIcon : ''}`}>
                <AppleMapsIcon />
              </div>
              <span className={`${styles.optionName} ${clientVariant ? styles.clientOptionName : ''}`}>Apple Maps</span>
              {clientVariant && <ChevronIcon />}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}

function GoogleMapsIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
        fill="#EA4335"
      />
      <circle cx="12" cy="9" r="2.5" fill="#B31412" />
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84L12 9V2z"
        fill="#4285F4"
      />
      <path d="M5 9c0 5.25 7 13 7 13V9H5z" fill="#34A853" />
      <path
        d="M12 9l5.59 4.84C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7v7z"
        fill="#FBBC04"
      />
    </svg>
  );
}

function AppleMapsIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="24" height="24" rx="5" fill="#52C850" />
      <path
        d="M12 5C9.24 5 7 7.24 7 10c0 4.5 5 9 5 9s5-4.5 5-9c0-2.76-2.24-5-5-5z"
        fill="white"
      />
      <circle cx="12" cy="10" r="2" fill="#52C850" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      className={styles.clientChevron}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="m9 18 6-6-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
